import * as _ from 'lodash';

import { useQuery as useQueryParams, useParams as useRouteParams } from '../../../../contexts/RouterContext';
import { omitClientOnlySnippetParams, type SnippetsRouteParams } from '../../../../hooks/api/snippets/shared';
import { getSnippets, getSnippetsQueryKey, type UseSnippetsOptions } from '../../../../hooks/api/snippets/useSnippets';
import { useInfiniteQuery } from '../../../../ReactQuery';
import type { SnippetsQueryParams, SnippetsResponse } from '../../../../../shared/snippets';

/**
 * useSnippetsForPackage is a hook that fetches snippets. It pulls any necessary query
 * information from the current URL.
 */
export default function useSnippetsForPackage(packageId: string, shouldFetch: boolean) {
  const routeParams = useRouteParams<SnippetsRouteParams>();
  const userRevisionLocator = `${routeParams.project_id}$${routeParams.revision_id}`;

  const [queryParams] = useQueryParams<SnippetsQueryParams>();

  const params = omitClientOnlySnippetParams({ ...queryParams, path: queryParams.path ?? '/', packageIds: [packageId] });
  const options: UseSnippetsOptions = {
    userRevisionLocator,
    // Omit pagination params since we're using infinite query.
    params: _.omit(params, 'page', 'pageSize'),
  };

  if (queryParams.olderRevisionLocator && routeParams.status) {
    options.compare = {
      olderRevisionLocator: queryParams.olderRevisionLocator,
      status: routeParams.status,
    };
  }

  return useInfiniteQuery<SnippetsResponse>({
    queryKey: getSnippetsQueryKey(options),
    // We know that options.params is defined here because we just set it above.
    queryFn: (context) => getSnippets({ ...options, params: { ...options.params!, page: context.pageParam ?? 1 } }),
    enabled: shouldFetch,
  });
}
